import { Injectable } from '@angular/core';
import { BydUserAccountInterface } from '@sixense/byd-auth-service';
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
import { Observable } from 'rxjs';
import { DsUserFront } from '../models/ds-user-front.model';
import { BydGenerateParams, BydModel } from '@sixense/byd-core-service';
import { DsNewUser } from '../models/ds-new-user.model';
import { DsUsersFiltersInterface } from '../interfaces/ds-users-filters.interface';
import { DsUpdateUserModel } from '../models/ds-update-user.model';

@Injectable({
  providedIn: 'root',
})
export class DsUserFrontService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Check if the user login is already used.
   *
   * @param login Email user
   * @param id the user id
   * @param number [id] optional User id. If provided, search for used login except if it is the current user's login.
   * @returns Observable type `<boolean>`
   */
  public checkUserLogin(login: string, id?: number): Observable<boolean> {
    this.path = '/users/v2/checklogin';
    const body = {
      id,
      login,
    };
    return this.post<boolean>({ body });
  }

  /**
   * Get selected project users with pagination.
   *
   * @param projectId Project ID
   * @param elementsPerPage Max number of elements per page
   * @param pageIndex Page index to query
   * @param filters Filters on list
   * @returns Observable type `BydModel<BydUserAccount>>`
   */
  public getProjectUsers(
    projectId: number,
    elementsPerPage = 20,
    pageIndex = 0,
    filters?: DsUsersFiltersInterface
  ): Observable<BydModel<DsUserFront>> {
    this.path = `/v3/projects/${projectId}/users`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    const params = BydGenerateParams<DsUsersFiltersInterface>(filters);
    return this.getModels<DsUserFront>({ headers, params });
  }

  /**
   * Get user by login.
   *
   * @param  Login Email user
   * @returns Observable type `<BydUserAccount>`
   */
  public getUserByLogin(Login: string): Observable<DsUserFront> {
    this.path = '/users/bylogin';
    const body = {
      login: Login,
    };
    return this.postModel<DsUserFront>({ body });
  }

  /**
   * Create a new user.
   *
   * @param user New user object
   * @returns Observable type `<BydUserAccount>`
   */
  public postUser(user: DsUserFront): Observable<DsUserFront> {
    this.path = '/users';
    const newUser = new DsNewUser(user);

    return this.postModel<DsUserFront>({ body: newUser });
  }

  /**
   * Update user.
   *
   * @param  user New user values
   * @returns Observable type `<BydUserAccount>`
   */
  public updateUser(user: DsUserFront): Observable<DsUserFront> {
    this.path = `/users/${user.accountId}`;
    const updatedUser = new DsUpdateUserModel(user);

    return this.putModel<DsUserFront>({ body: updatedUser });
  }

  /**
   * Create BydUserAccount instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `BydUserAccount` instance.
   */
  public createNewInstance(jsonObject: BydUserAccountInterface): DsUserFront {
    return new DsUserFront(jsonObject);
  }
}
